import toast from 'react-hot-toast';
import api, { sendReportEmail } from '../../../lib/api';
import { downloadBlob, downloadRepairBucketReportPdf, readBlobError } from '../../../lib/downloadBlob';
import { ROW_KIND } from '../utils/reportRows';

/** Email modal title / filename prefix — distinct from the short button-label mapping in ReportsView. */
export function getReportTypeReportLabel(reportType) {
  if (reportType === 'defect') return 'Defect Report';
  if (reportType === 'repair') return 'Repair Report';
  return 'Session Report';
}

/**
 * PDF export and email-send handlers for the Reports table, moved out of
 * ReportsView.jsx unchanged (Brief 59 Part 0 extraction). The archived
 * "hist" (`hr:`) branches these two functions used to carry are gone with
 * the rows they served (Brief 59 Part 5) — every row reaching these handlers
 * now has a real database row behind it.
 */
export function useReportExports() {
  const handleExportSessionReport = async (row, reportType) => {
    const loadingId = toast.loading('Generating PDF...');
    try {
      if (row.kind === ROW_KIND.FINALISED_BUCKET) {
        await downloadRepairBucketReportPdf(row.raw.id);
        toast.dismiss(loadingId);
        toast.success('PDF downloaded!');
        return;
      }

      const sessionId = row.raw.id;

      if (reportType === 'session') {
        const response = await api.get(`/sessions/${sessionId}/report.pdf`, { responseType: 'blob' });
        const contentDisposition = response.headers['content-disposition'];
        const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
        const filename = filenameMatch?.[1] || 'Test_Results_Report.pdf';
        downloadBlob(response.data, filename);
        toast.dismiss(loadingId);
        toast.success('PDF downloaded!');
        return;
      }

      if (reportType === 'defect') {
        const response = await api.get(`/sessions/${sessionId}/defect-report.pdf`, { responseType: 'blob' });
        const contentDisposition = response.headers['content-disposition'];
        const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
        const filename = filenameMatch?.[1] || 'Defect_Report.pdf';
        downloadBlob(response.data, filename);
        toast.dismiss(loadingId);
        toast.success('PDF downloaded!');
        return;
      }

      throw new Error(`Unknown report type: ${reportType}`);
    } catch (err) {
      console.error('PDF export failed', err);
      toast.dismiss(loadingId);
      toast.error(await readBlobError(err, 'Failed to download PDF'));
    }
  };

  const buildReportBlob = async (row, reportType) => {
    if (row.kind === ROW_KIND.FINALISED_BUCKET) {
      const response = await api.get(`/repairs/buckets/${row.raw.id}/report.pdf`, { responseType: 'blob' });
      return {
        blob: response.data,
        session: { site_name: row.siteLabel },
        emailExtras: {
          sessionIdForEmail: undefined,
          siteId: row.siteId,
          workOrderNumber: row.raw.workOrderNumber || '',
        },
      };
    }

    const sessionId = row.raw.id;
    const { data } = await api.get(`/sessions/${sessionId}/report-data`);
    const session = data?.session ?? null;

    // Full Test Results and Defect reports are server-rendered (same PDF the
    // download buttons use). report-data is still fetched above for every type
    // so `session` (site label for the email filename) is available regardless
    // of which branch runs. Unknown types throw — the old jsPDF repair path is gone.
    if (reportType === 'session') {
      const response = await api.get(`/sessions/${sessionId}/report.pdf`, { responseType: 'blob' });
      return {
        blob: response.data,
        session,
        emailExtras: { sessionIdForEmail: sessionId, siteId: undefined, workOrderNumber: undefined },
      };
    }

    if (reportType === 'defect') {
      const response = await api.get(`/sessions/${sessionId}/defect-report.pdf`, { responseType: 'blob' });
      return {
        blob: response.data,
        session,
        emailExtras: { sessionIdForEmail: sessionId, siteId: undefined, workOrderNumber: undefined },
      };
    }

    throw new Error(`Unknown report type: ${reportType}`);
  };

  const handleSendEmailReport = async (row, reportType, recipientEmail) => {
    const loadingId = toast.loading('Sending report...');
    try {
      const { blob, session, emailExtras } = await buildReportBlob(row, reportType);
      const reportTypeLabel = getReportTypeReportLabel(reportType);
      const siteLabel = (session?.site_name || session?.site_code || 'Site').replace(/\s+/g, '_');
      const fileName = `${reportTypeLabel.replace(/\s+/g, '_')}_${siteLabel}_${new Date().toISOString().split('T')[0]}.pdf`;
      await sendReportEmail({
        reportType,
        sessionId: emailExtras.sessionIdForEmail,
        siteId: emailExtras.siteId,
        workOrderNumber: emailExtras.workOrderNumber,
        recipientEmail,
        pdfBlob: blob,
        fileName,
      });
      toast.dismiss(loadingId);
      toast.success(`Report sent to ${recipientEmail}`);
    } catch (err) {
      console.error('Email report failed', err);
      toast.dismiss(loadingId);
      const apiError = err.response?.data;
      const msg = [apiError?.error, apiError?.hint, apiError?.code ? `(${apiError.code})` : '']
        .filter(Boolean)
        .join(' ');
      toast.error(msg || 'Failed to send report');
    }
  };

  return { handleExportSessionReport, buildReportBlob, handleSendEmailReport };
}
